import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Campaign } from '@interfaces';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/auth';
import { CampaignDetail } from 'src/app/modules/campaigns/models/campaign.models';
import { EnterMargins } from 'src/app/modules/renovate-contract-pymes/models/renovate-contract-pymes.model';

@Component({
  selector: 'app-monopoint-calculate-margin',
  templateUrl: './monopoint-calculate-margin.component.html',
  styleUrls: ['./monopoint-calculate-margin.component.scss']
})
export class MonopointCalculateMarginComponent implements OnInit, OnDestroy, OnChanges {
  public enterMarginsForm: FormGroup;
  public regexTwoDecimal = new RegExp(/^(\d{1,2}(\,\d{0,3})?|\.?\d{1,2})$/g);
  public showCalculation = false;
  public isUserSpanish = true;
  public fixPrice: boolean;
  public passThrough: boolean;
  public fijoFlex: boolean;
  public resultTP = [];
  public resultTE = [];
  public resultAI = [];
  public resultCG = [];
  public resultFijoFlex: number[]= [];
  public resultFijoFlexSimple: number[] = [];
  public campaignRealTE = [];
  public campaignKI = [];
  public campaignFI = [];
  public campaignGDOS = [];
  public campaignPFijo = [];
  public campaignPRef = [];
  public campaignTM = [];
  public campaignCTM = [];
  public campaignCPM = [];
  public period = 1;
  public fijoFlexSimple: boolean = false;

  private campaignCG = [];
  private campaignTE = [];
  private campaignTP = [];
  private campaignAI = [];
  private destroyed$: Subject<boolean> = new Subject<boolean>();

  @Input() public fee: string;
  @Input() public greaterEqual100: boolean;
  @Input() public geographicZone: string;
  @Input() public selectedCampaign: Campaign;
  @Input() public campaignDetail: CampaignDetail[];
  @Input() public enterMarginsDraft: EnterMargins;
  @Output() public enterMargins: EventEmitter<FormGroup> = new EventEmitter();

  constructor(private authService: AuthService,) { }

  ngOnInit(): void {
    this.isUserSpanish = this.authService.getUserData().country === 'ES';
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.greaterEqual100 && changes.fee) {
      if (this.authService.getUserData().country === 'ES') {
        this.period = this.fee !== '2.0TD' ? 6 : 3;
        this.buildForm(true);
      } else {
        this.checkPtPeriod();
        this.buildForm(false);
        this.enterMarginsForm.get('marginTEP1').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
          for(let i = 2; i < (this.period + 1); i++) {
            this.enterMarginsForm.get('marginTEP' + i).setValue(value, { emitEvent: false });
          }
        });
      }
    }
    if (changes.selectedCampaign) {
      this.fixPrice = this.selectedCampaign?.precioFijo;
      this.passThrough = this.selectedCampaign?.passThrough;
      this.fijoFlex = this.selectedCampaign?.fijoFlex;
    }
    if (changes.campaignDetail && this.campaignDetail?.length > 0) {
      if (changes.campaignDetail.previousValue) {
        this.enterMarginsForm.reset();
        this.showCalculation = false;
      }
      this.findFee();
    }
    if (changes.enterMarginsDraft) {
      if (this.enterMarginsDraft) {
        this.getEnterMarginsValue();
      } else {
        this.enterMarginsForm.reset();
        this.showCalculation = !this.enterMarginsForm.invalid;
      }
    }
  }

  private buildForm(marginTP: boolean): void {
    const marginTE = {};
    const maxValue = this.authService.getUserData().country === 'ES' ? (this.greaterEqual100 ? 24.999 : 49.999) : 25.999;
    for (let i = 0; i < this.period; i++) {
      marginTE['marginTEP' + (i + 1)] = new FormControl(null, [ Validators.required, Validators.min(0), Validators.max(maxValue), Validators.pattern('([0-9]{1,2}(,[0-9]{1,3})?)') ]);
    }
    const marginForm = {
      ...marginTE
    };
    if (marginTP) {
      marginForm['marginTP'] = new FormControl(null, [ Validators.required, Validators.min(0), Validators.max(24), Validators.pattern('([0-9]{1,2}(,[0-9]{1,3})?)') ]);
    }
    this.enterMarginsForm = new FormGroup(marginForm);
    this.enterMarginsForm.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.enterMargins.emit(this.enterMarginsForm);
    });
  }

  public calculateMargin(): void {
    this.showCalculation = !this.enterMarginsForm.invalid;
    this.resultTE = this.calcResultTE();
    if (this.isUserSpanish) {
      this.resultTP = this.calcResultTP();
    }
    this.resultAI = this.calcResultAI();
    this.resultCG = this.calcResultCG();
    if(this.fijoFlexSimple){
      this.resultFijoFlexSimple = this.calcResultFijoFlexSimple();
    } else {
      this.resultFijoFlex = this.calcResultFijoFlex();
    }
  }

  private calcResultTE(): number[] {
    const resultTE = [];
    this.campaignTE?.forEach((valTE, index) => {
      if (this.fee === '2.0TD' && index > 2) {
        resultTE.push(0);
      } else {
        const marginTePeriodName = `marginTEP${index + 1}`;
        let calc = 0;
        if (this.isUserSpanish) {
          calc = ((parseFloat(this.enterMarginsForm.get(marginTePeriodName)?.value?.toString().replace(',', '.')) / 1000) * this.campaignTM[index]) +
          (valTE / 1000);
        } else {
          calc = (parseFloat(this.enterMarginsForm.get(marginTePeriodName)?.value?.toString().replace(',', '.')) + valTE) / 1000;
        }
        const resultTEValue = this.enterMarginsForm.get(marginTePeriodName)?.value ? calc.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ] : null;
        resultTE.push(parseFloat(resultTEValue));
      }
    });
    return resultTE;
  }

  private calcResultAI(): number[] {
    const resultAI = [];
    this.campaignAI?.forEach((valAI, index) => {
      if (this.fee === '2.0TD' && index > 2) {
        resultAI.push(0);
      } else {
        const marginTePeriodName = `marginTEP${index + 1}`;
        let calc = 0;
        if (this.isUserSpanish) {
          calc = ((parseFloat(this.enterMarginsForm.get(marginTePeriodName)?.value?.toString().replace(',', '.')) / 1000) * ((1 + this.campaignCTM[index]) * (1 + this.campaignCPM[index]))) +
          (valAI / 1000);
        } else {
          calc = ((parseFloat(this.enterMarginsForm.get(marginTePeriodName)?.value?.toString().replace(',', '.')) + valAI) / 1000);
        }
        const resultAIValue = this.enterMarginsForm.get(marginTePeriodName)?.value ? calc.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ] : null;
        resultAI.push(parseFloat(resultAIValue));
      }
    });
    return resultAI;
  }

  private calcResultFijoFlexSimple(): number[] {
    const resultFijoFlexSimple = [];
      this.campaignPRef?.forEach((valPFijoSimple, index) => {
        if ((this.fee === '2.0TD' && index > 2) || !valPFijoSimple) {
          resultFijoFlexSimple.push(0);
        } else {
          const marginTePeriodName = `marginTEP${index + 1}`;
          let calc = null;
          if (this.isUserSpanish) {
            calc = ((parseFloat(this.enterMarginsForm.get(marginTePeriodName)?.value?.toString().replace(',', '.')) / 1000) * this.campaignTM[index]) +
            (valPFijoSimple / 1000);
          } else {
            calc = valPFijoSimple / 1000;
          }
          const resultPRefValue = this.enterMarginsForm.get(marginTePeriodName)?.value ? calc.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ] : null;
          resultFijoFlexSimple.push(parseFloat(resultPRefValue));
        }
      });
    return resultFijoFlexSimple;
  }

  private calcResultFijoFlex(): number[]{
    const resultFijoFlex = [];
      this.campaignPFijo?.forEach((valPFijo, index) => {
        if ((this.fee === '2.0TD' && index > 2) || !valPFijo) {
          resultFijoFlex.push(0);
        } else {
          const marginTePeriodName = `marginTEP${index + 1}`;
          let calc = null;
          if (this.isUserSpanish) {
            calc = ((parseFloat(this.enterMarginsForm.get(marginTePeriodName)?.value?.toString().replace(',', '.')) / 1000) * this.campaignTM[index]) +
            (valPFijo / 1000);
          } else {
            calc = (parseFloat(this.enterMarginsForm.get(marginTePeriodName)?.value?.toString().replace(',', '.')) / 1000) +
            (valPFijo / 1000);
          }
          const resultPFijoValue = this.enterMarginsForm.get(marginTePeriodName)?.value ? calc.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ] : null;
          resultFijoFlex.push(parseFloat(resultPFijoValue));
        }
      });
    return resultFijoFlex;
  }

  private calcResultTP(): number[] {
    const resultTP = [];
    this.campaignTP?.forEach((valTP, index) => {
      if (valTP === null) {
        resultTP.push(null);
      } else {
        const calc = parseFloat(this.enterMarginsForm.get('marginTP')?.value?.toString().replace(',', '.')) + valTP;
        resultTP.push(parseFloat(calc.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ]));
      }
    });
    return resultTP;
  }

  private calcResultCG(): number[] {
    const resultCG = [];
    this.campaignCG?.forEach((valCG, index) => {
      if (valCG === null) {
        resultCG.push(null);
      } else {
        if (this.isUserSpanish) {
          resultCG.push(+this.enterMarginsForm?.get('marginTEP' + (index + 1))?.value?.toString().replace(',', '.'));
        } else {
          resultCG.push(valCG + +this.enterMarginsForm?.get('marginTEP' + (index + 1))?.value?.toString().replace(',', '.'));
        }
      }
    });
    return resultCG;
  }

  private findFee(): void {
    if (this.fee.includes('S/C - ')) {
      this.fee = this.fee.replace('S/C - ', '');
    }
    const findFee = this.campaignDetail.find(a => a.zonasGeograficas.find(
      zone => zone.nombreZona === this.geographicZone
    ).tarifas.find(b => b.nombreTarifa === this.fee));
    const powerTerms = findFee?.zonasGeograficas.find(
      zone => zone.nombreZona === this.geographicZone
    ).tarifas.find(el => el.nombreTarifa === this.fee);
    this.resetCampaignData();
    for (let i = 0; i < 6; i++) {
      if (!this.isUserSpanish) {
        this.campaignTE.push(powerTerms[`precio_P${i + 1}`] === null ? null : powerTerms[`precio_P${i + 1}`]);       
      } else {
        this.campaignTE.push(powerTerms[`precioFinal_P${i + 1}`] === null ? null : powerTerms[`precioFinal_P${i + 1}`]);
      }
      this.campaignRealTE.push((powerTerms[ `tE_P${i + 1}` ] === null ? null : powerTerms[ `tE_P${i + 1}` ]) /1000);
      this.campaignTP.push(powerTerms[ `tP_P${i + 1}` ] === null ? null : powerTerms[ `tP_P${i + 1}` ]);
      this.campaignAI.push(powerTerms[ `ai_P${i + 1}` ] === null ? null : powerTerms[ `ai_P${i + 1}` ]);
      this.campaignKI.push(powerTerms[ `ki_P${i + 1}` ] === null ? null : powerTerms[ `ki_P${i + 1}` ]);
      this.campaignFI.push(powerTerms[ `fi_P${i + 1}` ] === null ? null : powerTerms[ `fi_P${i + 1}` ]);
      this.campaignCG.push(powerTerms[ `cG_P${i + 1}` ] === null ? null : powerTerms[ `cG_P${i + 1}` ]);
      this.campaignGDOS.push(powerTerms[ `gdOs_P${i + 1}` ] === null ? null : powerTerms[ `gdOs_P${i + 1}` ]);
      this.campaignPFijo.push(powerTerms[ `pFijo_P${i + 1}` ] === null ? null : powerTerms[ `pFijo_P${i + 1}` ]);
      if(powerTerms[ `pRef_P1` ]){
        this.fijoFlexSimple = true;
      } else{
        this.fijoFlexSimple = false;
      }
      this.campaignPRef.push(!powerTerms[ `pRef_P${i + 1}` ] ? null : powerTerms[ `pRef_P${i + 1}` ]);
      this.campaignTM.push(!powerTerms[ `tM_P${i + 1}` ] ? null : powerTerms[ `tM_P${i + 1}` ]);
      this.campaignCTM.push(!powerTerms[ `ctM_P${i + 1}` ] ? null : powerTerms[ `ctM_P${i + 1}` ]);
      this.campaignCPM.push(!powerTerms[ `cpM_P${i + 1}` ] ? null : powerTerms[ `cpM_P${i + 1}` ]);
    }
    if (this.enterMarginsForm.valid) {
      this.calculateMargin();
      this.enterMargins.emit(this.enterMarginsForm);
    }
  }

  private resetCampaignData(): void {
    this.campaignTE = [];
    this.campaignTP = [];
    this.campaignAI = [];
    this.campaignKI = [];
    this.campaignFI = [];
    this.campaignCG = [];
    this.campaignGDOS = [];
    this.campaignPFijo = [];
    this.campaignPRef = [];
    this.campaignTM = [];
    this.campaignCTM = [];
      
    this.campaignCPM = [];
  }

  private checkPtPeriod(): void {
    // if (this.fee.includes('Simples')) {
    //   this.period = 1;
    // } else if (this.fee.includes('Dupla')) {
    //   this.period = 2;
    // } else if (this.fee.includes('Tripla')) {
    //   this.period = 3;
    // } else if (this.fee.includes('Tetra')) {
      this.period = 4;
    // }
  }

  private getEnterMarginsValue(): void {
    this.enterMarginsForm.patchValue({
      marginTP: this.enterMarginsDraft?.marginTP?.toString().replace('.', ','),
      marginTEP1: this.enterMarginsDraft?.marginsTE?.p1?.toString().replace('.', ','),
      marginTEP2: this.enterMarginsDraft?.marginsTE?.p2?.toString().replace('.', ','),
      marginTEP3: this.enterMarginsDraft?.marginsTE?.p3?.toString().replace('.', ','),
      marginTEP4: this.enterMarginsDraft?.marginsTE?.p4?.toString().replace('.', ','),
      marginTEP5: this.enterMarginsDraft?.marginsTE?.p5?.toString().replace('.', ','),
      marginTEP6: this.enterMarginsDraft?.marginsTE?.p6?.toString().replace('.', ',')
    });
    Object.keys(this.enterMarginsForm.controls).forEach(key => {
      this.enterMarginsForm.get(key).markAsTouched();
    });
    if (this.enterMarginsForm.valid) {
      this.calculateMargin();
    }
  }
}
